import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import CchPipelineHistoryList from "@/components/admin/CchPipelineHistoryList";
import CchPipelineRunCard from "@/components/admin/CchPipelineRunCard";
import CchPipelineTriggerCard from "@/components/admin/CchPipelineTriggerCard";
import {
  CCH_PIPELINE_RUNS_QUERY_KEY,
  cchPipelineRunQueryKey,
  getCchPipelineRun,
  isCchRunInFlight,
  listCchPipelineRuns,
  type CchPipelineRun,
} from "@/services/cchApi";

const POLL_MS = 5000;

export default function CchPipelineTab() {
  const queryClient = useQueryClient();
  const [focusedRunId, setFocusedRunId] = useState<string | null>(null);
  const [showingTriggerForm, setShowingTriggerForm] = useState(true);

  const runs = useQuery({
    queryKey: CCH_PIPELINE_RUNS_QUERY_KEY,
    queryFn: () => listCchPipelineRuns({ limit: 10 }),
    refetchInterval: 60_000,
  });

  // Auto-focus a currently-in-flight run so a load the operator just left
  // running keeps its live card visible on return. Explicit selection wins.
  const inFlightInList = runs.data?.items.find((r) => isCchRunInFlight(r.status)) ?? null;
  const activeRunId =
    focusedRunId ??
    (!showingTriggerForm
      ? runs.data?.items[0]?.run_id ?? null
      : inFlightInList?.run_id ?? null);

  const run = useQuery({
    queryKey: activeRunId
      ? cchPipelineRunQueryKey(activeRunId)
      : ["cch", "pipeline", "run", "none"],
    queryFn: () => getCchPipelineRun(activeRunId as string),
    enabled: activeRunId !== null,
    refetchInterval: (query) =>
      isCchRunInFlight(query.state.data?.status) ? POLL_MS : false,
  });

  // When a run leaves the in-flight states the recent-runs list is stale, so
  // refresh it once rather than polling it faster.
  const previousStatus = useRef<CchPipelineRun["status"] | undefined>(undefined);
  useEffect(() => {
    const current = run.data?.status;
    if (
      isCchRunInFlight(previousStatus.current) &&
      current &&
      !isCchRunInFlight(current)
    ) {
      queryClient.invalidateQueries({ queryKey: CCH_PIPELINE_RUNS_QUERY_KEY });
    }
    previousStatus.current = current;
  }, [run.data?.status, queryClient]);

  const onTriggered = (triggered: CchPipelineRun) => {
    // Seed the cache so the activity renders at once, before the first poll.
    queryClient.setQueryData(cchPipelineRunQueryKey(triggered.run_id), triggered);
    setFocusedRunId(triggered.run_id);
    setShowingTriggerForm(false);
  };

  const openRun = (runId: string) => {
    setFocusedRunId(runId);
    setShowingTriggerForm(false);
  };

  const backToTrigger = () => {
    setFocusedRunId(null);
    setShowingTriggerForm(true);
  };

  // Showing the trigger form means: no run is focused AND no in-flight run has
  // auto-grabbed focus. The user can always return to it via "Back to trigger".
  const showTrigger = showingTriggerForm && activeRunId === null;

  return (
    <div className="space-y-5">
      {showTrigger ? (
        <CchPipelineTriggerCard onTriggered={onTriggered} />
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={backToTrigger}
              className="gap-2 text-muted-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to trigger
            </Button>
          </div>
          {run.data ? (
            <CchPipelineRunCard
              run={run.data}
              isFetching={run.isFetching}
              onRefresh={() => run.refetch()}
              onOpenRun={openRun}
            />
          ) : (
            <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              {run.isLoading ? "Loading run…" : "No run detail available."}
            </div>
          )}
        </div>
      )}

      <CchPipelineHistoryList
        activeRunId={activeRunId}
        onSelect={openRun}
      />
    </div>
  );
}
